import React, { useCallback, useRef, useState, useMemo } from 'react';
import classNames from 'classnames';
import { AspectRatioWrapper, IconHeart, IconPlay, ResponsiveImage } from '../../../components';

import css from './ListingImageGallery.module.css';

const ListingImageGalleryMobileHero = props => {
  const {
    images,
    imageVariants,
    index,
    onIndexChange,
    onOpenFullscreen,
    totalImages,
    intl,
    currentUser,
    listing,
    onToggleFavorites,
    videoUrl,
    optimizedImages,
  } = props;

  const heroStartRef = useRef(null);
  const heroSwipeRef = useRef(false);
  const heroRef = useRef(null);
  const didSwipeRef = useRef(false);
  const [heroDragX, setHeroDragX] = useState(0);
  const [heroIsDragging, setHeroIsDragging] = useState(false);

  const mediaItems = useMemo(() => {
    const imageItems = (images || []).map((image, i) => ({
      type: 'image',
      image,
      key: image.id?.uuid || `image-${i}`,
      imageIndex: i,
    }));

    return videoUrl
      ? [{ type: 'video', key: 'listing-video', videoUrl, image: images?.[0], imageIndex: -1 }, ...imageItems]
      : imageItems;
  }, [images, videoUrl]);

  const isFavorite = useMemo(
    () => currentUser?.attributes?.profile?.privateData?.favorites?.includes(listing?.id?.uuid),
    [currentUser, listing]
  );

  const showFavoriteButton = typeof onToggleFavorites === 'function';

  const handleHeroTouchStart = useCallback(e => {
    const touch = e.touches?.[0];
    if (!touch) return;
    heroStartRef.current = { x: touch.clientX, y: touch.clientY };
    heroSwipeRef.current = false;
    didSwipeRef.current = false;
    setHeroIsDragging(true);
  }, []);

  const handleHeroTouchMove = useCallback(
    e => {
      if (!heroStartRef.current) return;
      const touch = e.touches?.[0];
      if (!touch) return;
      const dx = touch.clientX - heroStartRef.current.x;
      const dy = touch.clientY - heroStartRef.current.y;
      if (!heroSwipeRef.current) {
        if (Math.abs(dx) > Math.abs(dy) && Math.abs(dx) > 6) {
          heroSwipeRef.current = true;
        } else {
          return;
        }
      }
      if (e.cancelable) {
        e.preventDefault();
      }
      const isFirst = index <= 0;
      const isLast = index >= mediaItems.length - 1;
      if ((isFirst && dx > 0) || (isLast && dx < 0)) {
        setHeroDragX(0);
        return;
      }
      setHeroDragX(dx);
    },
    [index, mediaItems.length]
  );

  const handleHeroTouchEnd = useCallback(() => {
    if (heroSwipeRef.current) {
      const threshold = Math.min(120, (heroRef.current?.offsetWidth || 0) * 0.2);
      if (Math.abs(heroDragX) > threshold) {
        const direction = heroDragX < 0 ? 1 : -1;
        const nextIndex = Math.max(0, Math.min(index + direction, mediaItems.length - 1));
        if (nextIndex !== index) {
          didSwipeRef.current = true;
          onIndexChange(nextIndex);
        }
      }
    }
    heroStartRef.current = null;
    heroSwipeRef.current = false;
    setHeroDragX(0);
    setHeroIsDragging(false);
  }, [heroDragX, index, mediaItems.length, onIndexChange]);

  return (
    <div className={css.mobileHero} ref={heroRef}>
      <AspectRatioWrapper width={4} height={3} className={css.itemWrapper}>
        <div
          className={css.mobileHeroViewport}
          onClick={() => {
            if (didSwipeRef.current) {
              didSwipeRef.current = false;
              return;
            }
            onOpenFullscreen();
          }}
          onTouchStart={handleHeroTouchStart}
          onTouchMove={handleHeroTouchMove}
          onTouchEnd={handleHeroTouchEnd}
        >
          {showFavoriteButton ? (
            <button
              type="button"
              aria-pressed={isFavorite}
              className={classNames(css.mobileFavoriteButton, {
                [css.favoritedIcon]: isFavorite,
              })}
              onClick={e => {
                e.preventDefault();
                e.stopPropagation();
                onToggleFavorites(isFavorite);
              }}
            >
              <IconHeart isFilled={isFavorite} />
            </button>
          ) : null}
          {totalImages > 0 ? (
            <button
              type="button"
              className={css.viewAll}
              onClick={e => {
                e.stopPropagation();
                onOpenFullscreen();
              }}
            >
              {intl.formatMessage(
                {
                  id: 'ListingImageGallery.photosCount',
                  defaultMessage: '{count, plural, one {# photo} other {# photos}}',
                },
                { count: totalImages }
              )}
            </button>
          ) : null}
          <div
            className={classNames(css.mobileHeroTrack, {
              [css.mobileHeroTrackDragging]: heroIsDragging,
            })}
            style={{
              transform: `translate3d(calc(-${index * 100}% + ${heroDragX}px), 0, 0)`,
            }}
          >
            {mediaItems.map((mediaItem, i) => (
              <div className={css.mobileHeroSlide} key={mediaItem.key}>
                {mediaItem.type === 'video' ? (
                  <div className={css.videoThumbnail}>
                    <ResponsiveImage
                      rootClassName={css.item}
                      image={mediaItem.image}
                      alt={intl.formatMessage({ id: 'ListingImageGallery.videoThumbnailAltText' })}
                      variants={imageVariants}
                      sizes="100vw"
                      loading="eager"
                      fetchPriority="high"
                      decoding="async"
                      optimizedImages={optimizedImages}
                      imageIndex={0}
                    />
                    <div className={css.videoThumbnailOverlay}>
                      <IconPlay />
                    </div>
                  </div>
                ) : (
                  <ResponsiveImage
                    rootClassName={css.item}
                    image={mediaItem.image}
                    alt={intl.formatMessage(
                      { id: 'ListingImageGallery.imageAltTextDel' },
                      { index: mediaItem.imageIndex + 1, count: totalImages }
                    )}
                    variants={imageVariants}
                    sizes="100vw"
                    optimizedImages={optimizedImages}
                    imageIndex={mediaItem.imageIndex}
                    {...(i === 0
                      ? {
                          loading: 'eager',
                          fetchPriority: 'high',
                          decoding: 'async',
                          preferPreviewCrop: true,
                        }
                      : {})}
                  />
                )}
              </div>
            ))}
          </div>
        </div>
      </AspectRatioWrapper>
    </div>
  );
};

export default ListingImageGalleryMobileHero;
